import { z } from "zod";

const COLLABO_LINK_RETURN_KEY = "collaboLinkReturn";
const COLLABO_LINK_RETURN_MAX_AGE_MS = 600 * 1000;
// Name/email are deliberately excluded: they never appear in the URL in the first place —
// the sign-in screen re-fetches them from GET /v3/collabo/portal/pending on mount instead.
const RETURN_QUERY_KEYS = ["collabo_flow"] as const;

const CollaboLinkReturnSchema = z.object({
  query: z.string(),
  savedAt: z.number(),
});

// Rebuilt from an allow-list so whatever is stored can only ever reproduce the link-mode
// sign-in query, never steer the redirect elsewhere.
export const toCollaboLinkQuery = (
  searchParams: Pick<URLSearchParams, "get">,
): string | null => {
  if (searchParams.get("collabo_flow") !== "portal_link") {
    return null;
  }
  const query = new URLSearchParams();
  for (const key of RETURN_QUERY_KEYS) {
    const value = searchParams.get(key);
    if (value) {
      query.set(key, value);
    }
  }
  return query.toString();
};

// Remembers the link-mode query across the password reset detour so the user can come back
// to the same pending link instead of restarting from Collabo Portal.
export const saveCollaboLinkReturn = (
  searchParams: Pick<URLSearchParams, "get">,
  now: number = Date.now(),
): void => {
  const query = toCollaboLinkQuery(searchParams);
  if (query === null) {
    return;
  }
  try {
    sessionStorage.setItem(
      COLLABO_LINK_RETURN_KEY,
      JSON.stringify({ query, savedAt: now }),
    );
  } catch {
    // Without storage the reset flow simply ends in normal mode.
  }
};

export const clearCollaboLinkReturn = (): void => {
  try {
    sessionStorage.removeItem(COLLABO_LINK_RETURN_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};

const readRaw = (): string | null => {
  try {
    return sessionStorage.getItem(COLLABO_LINK_RETURN_KEY);
  } catch {
    return null;
  }
};

const parseRaw = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// One-shot: the key is removed whether or not it is usable.
export const consumeCollaboLinkReturn = (
  now: number = Date.now(),
): string | null => {
  const raw = readRaw();
  clearCollaboLinkReturn();
  if (raw === null) {
    return null;
  }

  const parsed = CollaboLinkReturnSchema.safeParse(parseRaw(raw));
  if (!parsed.success) {
    return null;
  }
  const age = now - parsed.data.savedAt;
  if (age < 0 || age > COLLABO_LINK_RETURN_MAX_AGE_MS) {
    return null;
  }

  const query = toCollaboLinkQuery(new URLSearchParams(parsed.data.query));
  return query === null ? null : `/auth/sign-in?${query}`;
};
